/**
 * Applications Store Slice
 *
 * Zustand slice for Application Marketplace CRUD state management.
 * Handles applications (CRUD + archive/unarchive) and org application usage
 * limits.
 *
 * GW-8285: Session lifecycle state/actions (create/launch, list/search,
 * revoke/expire/renew, Session Companion Bar fields) moved to the
 * authoritative `sessionsSlice.ts` (GW-8255 FR-2/Phase 4, Task 28). This
 * slice no longer touches sessions at all — components needing session
 * state must use `useSessionsStore` (or select `SessionsSlice` fields
 * directly), not `useApplicationsStore`.
 *
 * GW-11452: TOS acceptance state/actions (`tosAcceptances`,
 * `checkTOSAcceptance`, `acceptTOSUser`, `acceptTOSOrg`) moved out of this
 * slice entirely — `useApplicationTOS` now owns TOS status directly against
 * `applicationsApi`'s canonical split-endpoint shape (GW-11433), mirroring
 * datasets' equivalent removal in GW-11453.
 */

import {
  clearError as clearOpError,
  clearErrorStatus,
  hasOperationLoaded,
  isOperationLoading,
  setError,
  setErrorStatus,
  setLoaded,
  setLoading,
} from '@gw/ui';
import {
  type Application,
  type ApplicationFilters,
  type CreateApplicationRequest,
  type OrganizationAppLimit,
  type PaginationMetadata,
  type UpdateApplicationRequest,
  ErrorStates,
  ErrorStatuses,
  LoadedTimestamps,
  LoadingStates,
} from '@gw/ui/types';

import { ApiError } from '@/api/apiError';
import { applicationsApi } from '@/api/applications';

import type { StateCreator } from 'zustand';

// ============================================================================
// Slice Interface
// ============================================================================

export interface ApplicationsSlice {
  // State properties
  applications: Application[];
  applicationsPagination: PaginationMetadata | null;
  applicationsSearchQuery: string;
  orgApplicationsPagination: PaginationMetadata | null;
  orgApplicationsSearchQuery: string;
  showArchivedApplications: boolean;
  currentApplication: Application | null;
  orgAppLimits: Record<string, OrganizationAppLimit>;
  isLoading: LoadingStates;
  hasLoaded: LoadingStates;
  lastLoaded: LoadedTimestamps;
  errors: ErrorStates;
  errorStatuses: ErrorStatuses;

  // Application management actions
  fetchApplications: (filters?: ApplicationFilters) => Promise<void>;
  forceRefreshApplications: () => Promise<void>;
  loadMoreApplications: () => Promise<void>;
  searchApplications: (query: string) => Promise<void>;
  resetApplicationsPagination: () => void;
  fetchOrgApplications: (orgId: string, filters?: ApplicationFilters) => Promise<void>;
  loadMoreOrgApplications: (orgId: string) => Promise<void>;
  searchOrgApplications: (orgId: string, query: string) => Promise<void>;
  resetOrgApplicationsPagination: () => void;
  fetchApplicationById: (appId: string) => Promise<void>;
  createApplication: (data: CreateApplicationRequest) => Promise<Application>;
  updateApplication: (appId: string, updates: UpdateApplicationRequest) => Promise<void>;
  deleteApplication: (appId: string) => Promise<void>;
  archiveApplication: (appId: string) => Promise<void>;
  unarchiveApplication: (appId: string) => Promise<void>;
  setShowArchivedApplications: (show: boolean) => void;
  uploadIcon: (appId: string, file: File) => Promise<string>;
  uploadScreenshot: (appId: string, file: File) => Promise<string>;

  // Organization admin actions
  setApplicationEnabled: (orgId: string, appId: string, enabled: boolean) => Promise<void>;

  // Utility actions
  clearError: (operation?: string) => void;
  clearCurrentApplication: () => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialApplicationsState = {
  applications: [],
  applicationsPagination: null,
  applicationsSearchQuery: '',
  orgApplicationsPagination: null,
  orgApplicationsSearchQuery: '',
  showArchivedApplications: false,
  currentApplication: null,
  orgAppLimits: {},
  isLoading: {},
  hasLoaded: {},
  lastLoaded: {},
  errors: {},
  errorStatuses: {},
};

// ============================================================================
// Slice Creator
// ============================================================================

export const createApplicationsSlice: StateCreator<ApplicationsSlice, [], [], ApplicationsSlice> = (
  set,
  get
) => ({
  ...initialApplicationsState,

  // ==========================================================================
  // Application Management
  // ==========================================================================

  fetchApplications: async (filters?: ApplicationFilters) => {
    const currentState = get();

    // Merge search query from state with filters
    const finalFilters: ApplicationFilters = {
      limit: 200, // Default limit for fresh list
      ...filters,
      search: filters?.search ?? currentState.applicationsSearchQuery,
    };

    // Guard: Don't fetch if already loading
    if (isOperationLoading(currentState.isLoading, 'fetchApplications')) {
      return;
    }

    // Guard: Return cached data if already loaded successfully with data
    // Allow retry if applications array is empty (possible failed previous load)
    if (
      hasOperationLoaded(currentState.hasLoaded, 'fetchApplications') &&
      currentState.applications.length > 0 &&
      !currentState.errors.fetchApplications
    ) {
      return;
    }

    set({
      isLoading: setLoading(currentState.isLoading, 'fetchApplications', true),
      errors: clearOpError(currentState.errors, 'fetchApplications'),
    });

    try {
      const response = await applicationsApi.listApplications(finalFilters);

      const state = get();
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'fetchApplications', true);

      set({
        applications: response.applications ?? [],
        applicationsPagination: response.pagination || null,
        isLoading: setLoading(state.isLoading, 'fetchApplications', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      console.error('[ApplicationsSlice] Failed to fetch applications:', error);

      const state = get();
      const errorMessage = error instanceof Error ? error.message : 'Failed to load applications';
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'fetchApplications', false);

      set({
        isLoading: setLoading(state.isLoading, 'fetchApplications', false),
        errors: setError(state.errors, 'fetchApplications', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    }
  },

  forceRefreshApplications: async () => {
    const currentState = get();

    // Clear cache to force new fetch
    set({
      hasLoaded: { ...currentState.hasLoaded, fetchApplications: false },
      applications: [],
      applicationsPagination: null,
      errors: clearOpError(currentState.errors, 'fetchApplications'),
    });

    // Fetch fresh
    await get().fetchApplications();
  },

  loadMoreApplications: async () => {
    const currentState = get();

    // Guard: Cannot load more if no pagination metadata or no next cursor
    if (!currentState.applicationsPagination || !currentState.applicationsPagination.nextCursor) {
      return;
    }

    // Guard: Don't load if already loading
    if (isOperationLoading(currentState.isLoading, 'loadMoreApplications')) {
      return;
    }

    set({
      isLoading: setLoading(currentState.isLoading, 'loadMoreApplications', true),
      errors: clearOpError(currentState.errors, 'loadMoreApplications'),
    });

    try {
      const filters: ApplicationFilters = {
        nextCursor: currentState.applicationsPagination.nextCursor,
        search: currentState.applicationsSearchQuery || undefined,
        limit: currentState.applicationsPagination.limit,
      };

      const response = await applicationsApi.listApplications(filters);

      const state = get();
      const loadedState = setLoaded(
        state.hasLoaded,
        state.lastLoaded,
        'loadMoreApplications',
        true
      );

      set({
        applications: [...state.applications, ...(response.applications ?? [])], // Append new applications
        applicationsPagination: response.pagination || null,
        isLoading: setLoading(state.isLoading, 'loadMoreApplications', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      console.error('[ApplicationsSlice] Failed to load more applications:', error);

      const state = get();
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to load more applications';
      const loadedState = setLoaded(
        state.hasLoaded,
        state.lastLoaded,
        'loadMoreApplications',
        false
      );

      set({
        isLoading: setLoading(state.isLoading, 'loadMoreApplications', false),
        errors: setError(state.errors, 'loadMoreApplications', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  searchApplications: async (query: string) => {
    const currentState = get();

    set({
      applicationsSearchQuery: query,
      isLoading: setLoading(currentState.isLoading, 'searchApplications', true),
      errors: clearOpError(currentState.errors, 'searchApplications'),
    });

    try {
      const filters: ApplicationFilters = {
        search: query || undefined,
        limit: 200, // Default limit for new search
      };

      const response = await applicationsApi.listApplications(filters);

      const state = get();
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'searchApplications', true);

      set({
        applications: response.applications ?? [],
        applicationsPagination: response.pagination || null,
        isLoading: setLoading(state.isLoading, 'searchApplications', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      console.error('[ApplicationsSlice] Failed to search applications:', error);

      const state = get();
      const errorMessage = error instanceof Error ? error.message : 'Failed to search applications';
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'searchApplications', false);

      set({
        isLoading: setLoading(state.isLoading, 'searchApplications', false),
        errors: setError(state.errors, 'searchApplications', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  resetApplicationsPagination: () => {
    set({
      applicationsPagination: null,
      applicationsSearchQuery: '',
    });
  },

  fetchOrgApplications: async (orgId: string, filters?: ApplicationFilters) => {
    const currentState = get();

    // Merge search query from state with filters
    const finalFilters: ApplicationFilters = {
      limit: 1000, // High limit to load all applications at once (preserves client-side search/filter)
      ...filters,
      search: filters?.search ?? currentState.orgApplicationsSearchQuery,
    };

    // Guard: Don't fetch if already loading
    if (isOperationLoading(currentState.isLoading, 'fetchOrgApplications')) {
      return;
    }

    set({
      isLoading: setLoading(currentState.isLoading, 'fetchOrgApplications', true),
      errors: clearOpError(currentState.errors, 'fetchOrgApplications'),
    });

    try {
      const response = await applicationsApi.listOrgApplications(orgId, finalFilters);

      const state = get();
      const loadedState = setLoaded(
        state.hasLoaded,
        state.lastLoaded,
        'fetchOrgApplications',
        true
      );

      set({
        applications: response.applications ?? [],
        orgApplicationsPagination: response.nextCursor
          ? {
              hasMore: !!response.nextCursor,
              nextCursor: response.nextCursor,
              total: response.applications?.length ?? 0,
              limit: finalFilters.limit ?? 20,
            }
          : null,
        isLoading: setLoading(state.isLoading, 'fetchOrgApplications', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      // 403 would be unexpected here — org_admin, global_admin, and app_admin can all list org apps (GW-4569)
      if (!(error instanceof ApiError && error.status === 403)) {
        console.error('[ApplicationsSlice] Failed to fetch org applications:', error);
      }

      const state = get();
      const errorMessage = error instanceof Error ? error.message : 'Failed to load applications';
      const loadedState = setLoaded(
        state.hasLoaded,
        state.lastLoaded,
        'fetchOrgApplications',
        false
      );

      set({
        isLoading: setLoading(state.isLoading, 'fetchOrgApplications', false),
        errors: setError(state.errors, 'fetchOrgApplications', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    }
  },

  loadMoreOrgApplications: async (orgId: string) => {
    const currentState = get();

    // Guard: Cannot load more if no pagination metadata or no next cursor
    if (
      !currentState.orgApplicationsPagination ||
      !currentState.orgApplicationsPagination.nextCursor
    ) {
      return;
    }

    // Guard: Don't load if already loading
    if (isOperationLoading(currentState.isLoading, 'loadMoreOrgApplications')) {
      return;
    }

    set({
      isLoading: setLoading(currentState.isLoading, 'loadMoreOrgApplications', true),
      errors: clearOpError(currentState.errors, 'loadMoreOrgApplications'),
    });

    try {
      const filters: ApplicationFilters = {
        nextCursor: currentState.orgApplicationsPagination.nextCursor,
        search: currentState.orgApplicationsSearchQuery || undefined,
        limit: currentState.orgApplicationsPagination.limit,
      };

      const response = await applicationsApi.listOrgApplications(orgId, filters);

      const state = get();
      const loadedState = setLoaded(
        state.hasLoaded,
        state.lastLoaded,
        'loadMoreOrgApplications',
        true
      );

      set({
        applications: [...state.applications, ...(response.applications ?? [])], // Append new applications
        orgApplicationsPagination: response.nextCursor
          ? {
              hasMore: !!response.nextCursor,
              nextCursor: response.nextCursor,
              total: state.applications.length + (response.applications?.length ?? 0),
              limit: currentState.orgApplicationsPagination?.limit ?? 20,
            }
          : null,
        isLoading: setLoading(state.isLoading, 'loadMoreOrgApplications', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      console.error('[ApplicationsSlice] Failed to load more org applications:', error);

      const state = get();
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to load more applications';
      const loadedState = setLoaded(
        state.hasLoaded,
        state.lastLoaded,
        'loadMoreOrgApplications',
        false
      );

      set({
        isLoading: setLoading(state.isLoading, 'loadMoreOrgApplications', false),
        errors: setError(state.errors, 'loadMoreOrgApplications', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  searchOrgApplications: async (orgId: string, query: string) => {
    const currentState = get();

    set({
      orgApplicationsSearchQuery: query,
      isLoading: setLoading(currentState.isLoading, 'searchOrgApplications', true),
      errors: clearOpError(currentState.errors, 'searchOrgApplications'),
    });

    try {
      const filters: ApplicationFilters = {
        search: query || undefined,
        limit: 1000, // High limit to load all applications at once (consistent with fetchOrgApplications)
      };

      const response = await applicationsApi.listOrgApplications(orgId, filters);

      const state = get();
      const loadedState = setLoaded(
        state.hasLoaded,
        state.lastLoaded,
        'searchOrgApplications',
        true
      );

      set({
        applications: response.applications ?? [],
        orgApplicationsPagination: response.nextCursor
          ? {
              hasMore: true,
              nextCursor: response.nextCursor,
              total: response.applications?.length ?? 0,
              limit: filters.limit ?? 20,
            }
          : null,
        isLoading: setLoading(state.isLoading, 'searchOrgApplications', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      console.error('[ApplicationsSlice] Failed to search org applications:', error);

      const state = get();
      const errorMessage = error instanceof Error ? error.message : 'Failed to search applications';
      const loadedState = setLoaded(
        state.hasLoaded,
        state.lastLoaded,
        'searchOrgApplications',
        false
      );

      set({
        isLoading: setLoading(state.isLoading, 'searchOrgApplications', false),
        errors: setError(state.errors, 'searchOrgApplications', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  resetOrgApplicationsPagination: () => {
    set({
      orgApplicationsPagination: null,
      orgApplicationsSearchQuery: '',
    });
  },

  fetchApplicationById: async (appId: string) => {
    const currentState = get();

    set({
      isLoading: setLoading(currentState.isLoading, 'fetchApplicationById', true),
      errors: clearOpError(currentState.errors, 'fetchApplicationById'),
      errorStatuses: clearErrorStatus(currentState.errorStatuses, 'fetchApplicationById'),
    });

    try {
      const application = await applicationsApi.getApplication(appId);

      const state = get();
      const loadedState = setLoaded(
        state.hasLoaded,
        state.lastLoaded,
        'fetchApplicationById',
        true
      );

      set({
        currentApplication: application,
        isLoading: setLoading(state.isLoading, 'fetchApplicationById', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
        errorStatuses: clearErrorStatus(state.errorStatuses, 'fetchApplicationById'),
      });
    } catch (error) {
      console.error('[ApplicationsSlice] Failed to fetch application:', error);

      const state = get();
      const errorMessage = error instanceof Error ? error.message : 'Failed to load application';
      // Extract HTTP status code from ApiError
      const errorStatus = error instanceof ApiError ? error.status : undefined;
      const loadedState = setLoaded(
        state.hasLoaded,
        state.lastLoaded,
        'fetchApplicationById',
        false
      );

      set({
        isLoading: setLoading(state.isLoading, 'fetchApplicationById', false),
        errors: setError(state.errors, 'fetchApplicationById', errorMessage),
        errorStatuses: setErrorStatus(state.errorStatuses, 'fetchApplicationById', errorStatus),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  createApplication: async (data: CreateApplicationRequest) => {
    const currentState = get();

    set({
      isLoading: setLoading(currentState.isLoading, 'createApplication', true),
      errors: clearOpError(currentState.errors, 'createApplication'),
    });

    try {
      const newApplication = await applicationsApi.createApplication(data);

      const state = get();
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'createApplication', true);

      set({
        applications: [...state.applications, newApplication],
        isLoading: setLoading(state.isLoading, 'createApplication', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });

      return newApplication;
    } catch (error) {
      console.error('[ApplicationsSlice] Failed to create application:', error);

      const state = get();
      const errorMessage = error instanceof Error ? error.message : 'Failed to create application';
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'createApplication', false);

      set({
        isLoading: setLoading(state.isLoading, 'createApplication', false),
        errors: setError(state.errors, 'createApplication', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  updateApplication: async (appId: string, updates: UpdateApplicationRequest) => {
    const currentState = get();

    set({
      isLoading: setLoading(currentState.isLoading, 'updateApplication', true),
      errors: clearOpError(currentState.errors, 'updateApplication'),
    });

    try {
      const updatedApplication = await applicationsApi.updateApplication(appId, updates);

      const state = get();
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'updateApplication', true);

      set({
        applications: state.applications.map((app) =>
          app.appId === appId ? updatedApplication : app
        ),
        currentApplication:
          state.currentApplication?.appId === appId ? updatedApplication : state.currentApplication,
        isLoading: setLoading(state.isLoading, 'updateApplication', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      console.error('[ApplicationsSlice] Failed to update application:', error);

      const state = get();
      const errorMessage = error instanceof Error ? error.message : 'Failed to update application';
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'updateApplication', false);

      set({
        isLoading: setLoading(state.isLoading, 'updateApplication', false),
        errors: setError(state.errors, 'updateApplication', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  deleteApplication: async (appId: string) => {
    const currentState = get();

    set({
      isLoading: setLoading(currentState.isLoading, 'deleteApplication', true),
      errors: clearOpError(currentState.errors, 'deleteApplication'),
    });

    try {
      await applicationsApi.deleteApplication(appId);

      const state = get();
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'deleteApplication', true);

      set({
        applications: state.applications.filter((app) => app.appId !== appId),
        currentApplication:
          state.currentApplication?.appId === appId ? null : state.currentApplication,
        isLoading: setLoading(state.isLoading, 'deleteApplication', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      console.error('[ApplicationsSlice] Failed to delete application:', error);

      const state = get();
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete application';
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'deleteApplication', false);

      set({
        isLoading: setLoading(state.isLoading, 'deleteApplication', false),
        errors: setError(state.errors, 'deleteApplication', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  archiveApplication: async (appId: string) => {
    const currentState = get();

    set({
      isLoading: setLoading(currentState.isLoading, 'archiveApplication', true),
      errors: clearOpError(currentState.errors, 'archiveApplication'),
    });

    try {
      const archivedApplication = await applicationsApi.archiveApplication(appId);

      const state = get();
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'archiveApplication', true);

      set({
        applications: state.applications.map((app) =>
          app.appId === appId ? archivedApplication : app
        ),
        currentApplication:
          state.currentApplication?.appId === appId
            ? archivedApplication
            : state.currentApplication,
        isLoading: setLoading(state.isLoading, 'archiveApplication', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      console.error('[ApplicationsSlice] Failed to archive application:', error);

      const state = get();
      const errorMessage = error instanceof Error ? error.message : 'Failed to archive application';
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'archiveApplication', false);

      set({
        isLoading: setLoading(state.isLoading, 'archiveApplication', false),
        errors: setError(state.errors, 'archiveApplication', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  unarchiveApplication: async (appId: string) => {
    const currentState = get();

    set({
      isLoading: setLoading(currentState.isLoading, 'unarchiveApplication', true),
      errors: clearOpError(currentState.errors, 'unarchiveApplication'),
    });

    try {
      const restoredApplication = await applicationsApi.unarchiveApplication(appId);

      const state = get();
      const loadedState = setLoaded(
        state.hasLoaded,
        state.lastLoaded,
        'unarchiveApplication',
        true
      );

      set({
        applications: state.applications.map((app) =>
          app.appId === appId ? restoredApplication : app
        ),
        currentApplication:
          state.currentApplication?.appId === appId
            ? restoredApplication
            : state.currentApplication,
        isLoading: setLoading(state.isLoading, 'unarchiveApplication', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      console.error('[ApplicationsSlice] Failed to unarchive application:', error);

      const state = get();
      const errorMessage = error instanceof Error ? error.message : 'Failed to restore application';
      const loadedState = setLoaded(
        state.hasLoaded,
        state.lastLoaded,
        'unarchiveApplication',
        false
      );

      set({
        isLoading: setLoading(state.isLoading, 'unarchiveApplication', false),
        errors: setError(state.errors, 'unarchiveApplication', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  setShowArchivedApplications: (show: boolean) => {
    set({ showArchivedApplications: show });
  },

  uploadIcon: async (appId: string, file: File) => {
    const currentState = get();

    set({
      isLoading: setLoading(currentState.isLoading, 'uploadIcon', true),
      errors: clearOpError(currentState.errors, 'uploadIcon'),
    });

    try {
      const response = await applicationsApi.uploadIcon(appId, file);

      const state = get();
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'uploadIcon', true);

      // Update application with new icon URL
      set({
        applications: state.applications.map((app) =>
          app.appId === appId ? { ...app, iconUrl: response.url } : app
        ),
        currentApplication:
          state.currentApplication?.appId === appId
            ? { ...state.currentApplication, iconUrl: response.url }
            : state.currentApplication,
        isLoading: setLoading(state.isLoading, 'uploadIcon', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });

      return response.url;
    } catch (error) {
      console.error('[ApplicationsSlice] Failed to upload icon:', error);

      const state = get();
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload icon';
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'uploadIcon', false);

      set({
        isLoading: setLoading(state.isLoading, 'uploadIcon', false),
        errors: setError(state.errors, 'uploadIcon', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  uploadScreenshot: async (appId: string, file: File) => {
    const currentState = get();

    set({
      isLoading: setLoading(currentState.isLoading, 'uploadScreenshot', true),
      errors: clearOpError(currentState.errors, 'uploadScreenshot'),
    });

    try {
      const response = await applicationsApi.uploadScreenshot(appId, file);

      const state = get();
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'uploadScreenshot', true);

      // Update application with new screenshot URL
      set({
        applications: state.applications.map((app) =>
          app.appId === appId ? { ...app, screenshots: [...app.screenshots, response.url] } : app
        ),
        currentApplication:
          state.currentApplication?.appId === appId
            ? {
                ...state.currentApplication,
                screenshots: [...state.currentApplication.screenshots, response.url],
              }
            : state.currentApplication,
        isLoading: setLoading(state.isLoading, 'uploadScreenshot', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });

      return response.url;
    } catch (error) {
      console.error('[ApplicationsSlice] Failed to upload screenshot:', error);

      const state = get();
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload screenshot';
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'uploadScreenshot', false);

      set({
        isLoading: setLoading(state.isLoading, 'uploadScreenshot', false),
        errors: setError(state.errors, 'uploadScreenshot', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  // ==========================================================================
  // TOS Management — GW-11452: moved out of this slice. `useApplicationTOS`
  // now owns fetching/accepting TOS status directly against
  // `applicationsApi`'s canonical split-endpoint shape (GW-11433), mirroring
  // datasets' equivalent removal in GW-11453 (there is no dedicated datasets
  // TOS store slice either). See `src/hooks/useApplicationTOS.ts`.
  // ==========================================================================

  // ==========================================================================
  // Organization Admin
  // ==========================================================================

  setApplicationEnabled: async (orgId: string, appId: string, enabled: boolean) => {
    const currentState = get();

    set({
      isLoading: setLoading(currentState.isLoading, 'setApplicationEnabled', true),
      errors: clearOpError(currentState.errors, 'setApplicationEnabled'),
    });

    try {
      // GW-473: Backend returns simplified EnabledResponse (orgId, applicationId, enabled, updatedAt)
      // but we need to store a complete OrganizationAppLimit. Merge with existing state or defaults.
      const apiResponse = await applicationsApi.setApplicationEnabled(orgId, appId, { enabled });

      const state = get();
      const cacheKey = `${orgId}:${appId}`;
      const existingLimits = state.orgAppLimits[cacheKey];

      // Create complete OrganizationAppLimit by merging API response with existing limits or defaults
      const completeLimits: OrganizationAppLimit = {
        // Use existing values as base, or defaults if no existing limits
        orgId: apiResponse.orgId ?? existingLimits?.orgId ?? orgId,
        applicationId: apiResponse.applicationId ?? existingLimits?.applicationId ?? appId,
        maxTokensPerMember: existingLimits?.maxTokensPerMember ?? null, // Preserve or default to no limit
        timePeriodAmount: existingLimits?.timePeriodAmount ?? 1, // Preserve or default to 1
        timePeriodUnit: existingLimits?.timePeriodUnit ?? 'month', // Preserve or default to month
        updatedBy:
          (apiResponse as { updatedBy?: string }).updatedBy ?? existingLimits?.updatedBy ?? '',
        // Update with new values from API response
        enabled: apiResponse.enabled,
        updatedAt: apiResponse.updatedAt,
      };

      const loadedState = setLoaded(
        state.hasLoaded,
        state.lastLoaded,
        'setApplicationEnabled',
        true
      );

      set({
        orgAppLimits: { ...state.orgAppLimits, [cacheKey]: completeLimits },
        isLoading: setLoading(state.isLoading, 'setApplicationEnabled', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      console.error('[ApplicationsSlice] Failed to set application enabled:', error);

      const state = get();
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to update application status';
      const loadedState = setLoaded(
        state.hasLoaded,
        state.lastLoaded,
        'setApplicationEnabled',
        false
      );

      set({
        isLoading: setLoading(state.isLoading, 'setApplicationEnabled', false),
        errors: setError(state.errors, 'setApplicationEnabled', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  // ==========================================================================
  // Utility Actions
  // ==========================================================================

  clearError: (operation?: string) => {
    const currentState = get();

    if (operation) {
      set({
        errors: clearOpError(currentState.errors, operation),
        errorStatuses: clearErrorStatus(currentState.errorStatuses, operation),
      });
    } else {
      set({ errors: {}, errorStatuses: {} });
    }
  },

  clearCurrentApplication: () => {
    set({ currentApplication: null });
  },
});
